import { Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { GLOBAL_REGION_LABEL, parseRegion } from "../../app/utils/country-flags";
import { SiteService } from "../../app/services/site.service";
import { UserService } from "../../app/services/user.service";
import {
  SITE_PICKER_DEFAULT_HEADER_OFFSET,
  computeSitePickerPanelPosition,
} from "./site-picker-panel-position";

export interface SitePickerItem {
  item_id: number | string;
  item_text: string;
  region?: string;
}

export interface SitePickerGroup {
  id: string;
  label: string;
  flagCodes: string[];
  icon?: "star" | "globe";
  items: SitePickerItem[];
  emptyMessage: string;
}

@Component({
  selector: "app-site-picker",
  templateUrl: "./site-picker.component.html",
  styleUrls: ["./site-picker.component.scss"],
})
export class SitePickerComponent implements OnDestroy {
  @HostBinding("class.site-combo-v2") readonly siteComboV2Class = true;

  @Input() sites: SitePickerItem[] = [];
  @Input() selected: SitePickerItem[] | null = [];
  @Input() disabled = false;
  @Input() favoriteIds: Array<number | string> = [];
  @Input() showFavorites = true;

  @Output() selectedChange = new EventEmitter<SitePickerItem[] | null>();
  @Output() favoriteIdsChange = new EventEmitter<Array<number | string>>();
  @Output() apply = new EventEmitter<void>();
  @Output() favoriteError = new EventEmitter<string>();

  @ViewChild("sitePickerRoot") sitePickerRoot: ElementRef;
  @ViewChild("sitePickerFieldButton") sitePickerFieldButton: ElementRef;
  @ViewChild("sitePickerSearchInput") sitePickerSearchInput: ElementRef;

  public sitePickerOpen = false;
  public sitePickerSearch = "";
  public sitePickerPanelStyle: { [key: string]: string } | null = null;
  public sitePickerTwoColumns = false;
  public sitePickerCollapsed: { [groupId: string]: boolean } = {
    favorites: false,
    global: false,
  };

  constructor(
    private translateService: TranslateService,
    private userService: UserService,
    private siteService: SiteService,
  ) {}

  get selectedSites(): SitePickerItem[] {
    return this.selected || [];
  }

  get sitePickerFieldText(): string {
    const selected = this.selectedSites;
    if (!selected || selected.length === 0) {
      return this.translateService.instant("site");
    }

    const names = selected
      .map((site) => String(site?.item_text || "").trim())
      .filter((name) => name.length > 0);

    if (names.length === 0) {
      return this.translateService.instant("site");
    }

    if (names.length === 1) {
      return names[0];
    }

    const maxChars = 40;
    let visibleText = "";
    let visibleCount = 0;

    for (let i = 0; i < names.length; i++) {
      const nextText = visibleText ? `${visibleText}, ${names[i]}` : names[i];
      const remaining = names.length - (i + 1);
      const suffix = remaining > 0 ? ` +${remaining}` : "";

      if ((nextText + suffix).length <= maxChars) {
        visibleText = nextText;
        visibleCount = i + 1;
      } else {
        break;
      }
    }

    if (visibleCount === 0) {
      return `${names[0]} +${names.length - 1}`;
    }

    if (visibleCount < names.length) {
      return `${visibleText} +${names.length - visibleCount}`;
    }

    return visibleText;
  }

  get sitePickerGroups(): SitePickerGroup[] {
    const orderedSites = [...(this.sites || [])].sort((a, b) => String(a.item_text).localeCompare(String(b.item_text)));

    const sitesByRegion = orderedSites.reduce((groups, site) => {
      const regionValue = String(site.region || "").trim();
      const region = !regionValue || regionValue.toLowerCase() === "global" ? GLOBAL_REGION_LABEL : regionValue;
      if (!groups[region]) {
        groups[region] = [];
      }
      groups[region].push(site);
      return groups;
    }, {} as { [region: string]: SitePickerItem[] });

    const regionNames = Object.keys(sitesByRegion)
      .filter((region) => region !== GLOBAL_REGION_LABEL)
      .sort((a, b) => parseRegion(a).name.localeCompare(parseRegion(b).name));

    const groups: SitePickerGroup[] = [];

    if (this.showFavorites) {
      const favoriteSites = orderedSites.filter((site) => this.favoriteIds.includes(site.item_id));
      const favoriteLabel = this.translateService.instant("favorites");
      groups.push({
        id: "favorites",
        label: favoriteLabel,
        flagCodes: [],
        icon: "star",
        items: this.filterSitesBySearch(favoriteSites, favoriteLabel),
        emptyMessage: favoriteSites.length === 0 ? "No favorite sites yet" : "No favorite match",
      });
    }

    groups.push({
      id: "global",
      label: GLOBAL_REGION_LABEL,
      flagCodes: [],
      icon: "globe",
      items: this.filterSitesBySearch(sitesByRegion[GLOBAL_REGION_LABEL] || [], GLOBAL_REGION_LABEL),
      emptyMessage: "No site match",
    });

    regionNames.forEach((region) => {
      const parsed = parseRegion(region);
      groups.push({
        id: `region-${region}`,
        label: parsed.name || region,
        flagCodes: parsed.codes,
        items: this.filterSitesBySearch(sitesByRegion[region], parsed.name || region),
        emptyMessage: "No site match",
      });
    });

    return groups;
  }

  get sitePickerSummaryText(): string {
    return `${this.selectedSites.length} / ${(this.sites || []).length}`;
  }

  isGroupCollapsed(groupId: string): boolean {
    return !!this.sitePickerCollapsed[groupId];
  }

  toggleSitePicker(event?: Event): void {
    event?.stopPropagation();

    if (this.disabled) {
      return;
    }

    this.sitePickerOpen = !this.sitePickerOpen;
    if (this.sitePickerOpen) {
      this.sitePickerSearch = "";
      this.updateSitePickerPanelPosition();
      this.focusSitePickerSearchInput();
    } else {
      this.sitePickerPanelStyle = null;
    }
    this.setSitePickerBodyState(this.sitePickerOpen);
  }

  private focusSitePickerSearchInput(): void {
    setTimeout(() => {
      const inputElement = this.sitePickerSearchInput?.nativeElement as HTMLInputElement;
      inputElement?.focus();
    });
  }

  closeSitePicker(): void {
    this.sitePickerOpen = false;
    this.sitePickerPanelStyle = null;
    this.setSitePickerBodyState(false);
  }

  @HostListener("window:resize")
  @HostListener("window:scroll")
  onViewportChange(): void {
    if (!this.sitePickerOpen) {
      return;
    }

    this.updateSitePickerPanelPosition();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.sitePickerOpen) {
      return;
    }

    const rootElement = this.sitePickerRoot?.nativeElement as HTMLElement;
    const target = event?.target as Node;

    if (!rootElement || !target) {
      this.closeSitePicker();
      return;
    }

    if (!rootElement.contains(target)) {
      this.closeSitePicker();
    }
  }

  onSitePickerSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.sitePickerSearch = target?.value || "";
  }

  toggleSiteGroup(groupItems: SitePickerItem[], event?: Event): void {
    event?.stopPropagation();

    if (this.disabled || !groupItems || groupItems.length === 0) {
      return;
    }

    const selected = [...this.selectedSites];
    const selectedIds = new Set(selected.map((site) => site.item_id));
    const allSelected = groupItems.every((site) => selectedIds.has(site.item_id));

    let next: SitePickerItem[];
    if (allSelected) {
      const groupIds = new Set(groupItems.map((site) => site.item_id));
      next = selected.filter((site) => !groupIds.has(site.item_id));
    } else {
      groupItems.forEach((site) => {
        if (!selectedIds.has(site.item_id)) {
          selected.push(site);
          selectedIds.add(site.item_id);
        }
      });
      next = selected;
    }

    this.emitSelected(next);
  }

  toggleSiteSelection(site: SitePickerItem, event?: Event): void {
    event?.stopPropagation();

    if (this.disabled) {
      return;
    }

    const selected = [...this.selectedSites];
    const index = selected.findIndex((selectedSite) => selectedSite.item_id === site.item_id);

    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(site);
    }

    this.emitSelected(selected);
  }

  selectAllSites(): void {
    if (this.disabled) {
      return;
    }

    this.emitSelected([...(this.sites || [])]);
  }

  clearAllSites(): void {
    if (this.disabled) {
      return;
    }

    this.emitSelected([]);
  }

  isSiteSelected(site: SitePickerItem): boolean {
    return this.selectedSites.some((selectedSite) => selectedSite.item_id === site.item_id);
  }

  collapseSiteGroups(): void {
    const collapsed: { [groupId: string]: boolean } = {};
    this.sitePickerGroups.forEach((group) => {
      collapsed[group.id] = true;
    });
    this.sitePickerCollapsed = collapsed;
  }

  applySitePicker(): void {
    this.apply.emit();
    this.closeSitePicker();
  }

  getSiteGroupSelectedCount(groupItems: SitePickerItem[]): number {
    return (groupItems || []).filter((site) => this.isSiteSelected(site)).length;
  }

  getSiteGroupState(groupItems: SitePickerItem[]): "off" | "part" | "on" {
    if (!groupItems || groupItems.length === 0) {
      return "off";
    }

    const selectedCount = groupItems.filter((site) => this.isSiteSelected(site)).length;
    if (selectedCount === 0) {
      return "off";
    }
    if (selectedCount === groupItems.length) {
      return "on";
    }
    return "part";
  }

  toggleSiteGroupCollapsed(groupId: string, event?: Event): void {
    event?.stopPropagation();
    this.sitePickerCollapsed = {
      ...this.sitePickerCollapsed,
      [groupId]: !this.sitePickerCollapsed[groupId],
    };
  }

  isSiteFavorite(site: SitePickerItem): boolean {
    return this.favoriteIds.includes(site.item_id);
  }

  toggleSiteFavorite(site: SitePickerItem, event: Event): void {
    event.stopPropagation();

    if (!this.showFavorites) {
      return;
    }

    const previousFavoriteIds = this.favoriteIds;

    let nextFavoriteIds: Array<number | string>;
    if (this.isSiteFavorite(site)) {
      nextFavoriteIds = this.favoriteIds.filter((id) => id !== site.item_id);
    } else {
      nextFavoriteIds = [...this.favoriteIds, site.item_id];
    }

    this.favoriteIdsChange.emit(nextFavoriteIds);

    this.userService.updateFavoriteSites(nextFavoriteIds).toPromise().then(() => {
      this.siteService.clearSitesCache();
    }).catch((error) => {
      console.error("Error updating favorite sites:", error);
      this.favoriteIdsChange.emit(previousFavoriteIds);
      this.favoriteError.emit(this.translateService.instant("error_updating_favorite_sites"));
    });
  }

  trackBySiteId(index: number, site: SitePickerItem): number | string {
    return site.item_id;
  }

  trackBySiteGroupId(index: number, group: { id: string }): string {
    return group.id;
  }

  ngOnDestroy(): void {
    this.setSitePickerBodyState(false);
    this.sitePickerPanelStyle = null;
  }

  private emitSelected(selected: SitePickerItem[]): void {
    this.selectedChange.emit(selected.length > 0 ? selected : null);
  }

  private filterSitesBySearch(items: SitePickerItem[], groupLabel: string): SitePickerItem[] {
    const query = this.normalizeSiteText(this.sitePickerSearch);
    if (!query) {
      return items;
    }

    if (this.normalizeSiteText(groupLabel).includes(query)) {
      return items;
    }

    return items.filter((site) => {
      const regionName = parseRegion(String(site.region || "")).name;
      return this.normalizeSiteText(String(site.item_text)).includes(query)
        || this.normalizeSiteText(regionName).includes(query);
    });
  }

  private normalizeSiteText(value: string): string {
    return (value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  private setSitePickerBodyState(isOpen: boolean): void {
    if (typeof document === "undefined" || !document.body) {
      return;
    }

    if (isOpen) {
      document.body.classList.add("site-picker-open");
      return;
    }

    document.body.classList.remove("site-picker-open");
  }

  private updateSitePickerPanelPosition(): void {
    const fieldElement = this.sitePickerFieldButton?.nativeElement as HTMLElement;
    if (!fieldElement) {
      return;
    }

    const rect = fieldElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const result = computeSitePickerPanelPosition({
      fieldRect: {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
      },
      viewportWidth,
      viewportHeight,
      headerOffset: this.getFixedHeaderOffset(),
    });

    this.sitePickerTwoColumns = result.twoColumns;
    this.sitePickerPanelStyle = result.style;
  }

  private getFixedHeaderOffset(): number {
    if (typeof document === "undefined") {
      return SITE_PICKER_DEFAULT_HEADER_OFFSET;
    }

    const header = document.querySelector(".app-header") as HTMLElement | null;
    if (!header) {
      return SITE_PICKER_DEFAULT_HEADER_OFFSET;
    }

    const headerRect = header.getBoundingClientRect();
    return Math.max(SITE_PICKER_DEFAULT_HEADER_OFFSET, Math.round(headerRect.bottom));
  }

  @HostListener("document:keydown.escape")
  onEscapePress(): void {
    this.closeSitePicker();
  }
}
